import React, { useState, useEffect, useMemo } from 'react';
import type { Order, OrderStatus } from '../../types';
import { getCardItemsWithTargets, getEffectiveQuantityCompletedForBatch, getEffectiveQuantityServedForBatch } from '../../lib/orderUtils';
import ClockIcon from '../icons/ClockIcon';
import CheckCircleIcon from '../icons/CheckCircleIcon';
import BuffetTimer from '../pos/BuffetTimer';


export interface ServiceTicket {
  order: Order;
  batchIndex?: number;
  batchId?: string;
  showCompleteTable?: boolean;
}

interface OrderStatusColumnProps {
  title: string;
  orders?: Order[];
  tickets?: ServiceTicket[];
  onUpdateStatus: (orderId: string, status: OrderStatus) => void;
  onUpdateItemStatus?: (orderId: string, itemIndex: number, served: boolean, targetQty?: number, batchIndex?: number, targetQtyForCard?: number) => void;
}

const OrderCard: React.FC<{
  order: Order;
  /** When defined, 0 = show only initial batch; 1 = running order (delta items only) */
  visibleBatchIndex?: number;
  /** Show "Complete Table" button on this card (true for main card or when only running card exists) */
  showCompleteTable?: boolean;
  onUpdateStatus: (orderId: string, status: OrderStatus) => void;
  onUpdateItemStatus?: (orderId: string, itemIndex: number, served: boolean, targetQty?: number, batchIndex?: number, targetQtyForCard?: number) => void;
}> = ({ order, visibleBatchIndex, showCompleteTable = true, onUpdateStatus, onUpdateItemStatus }) => {
    const [elapsedTime, setElapsedTime] = useState('');

    useEffect(() => {
        const updateElapsedTime = () => {
            const now = Date.now();
            const diff = now - order.timestamp;
            const minutes = Math.floor(diff / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            setElapsedTime(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        };
        
        updateElapsedTime();
        const interval = setInterval(updateElapsedTime, 1000);

        return () => clearInterval(interval);
    }, [order.timestamp]);
    
    const isBuffet = order.orderType === 'buffet';

    const isDiscountItem = (item: Order['items'][0]) =>
      (typeof item.price === 'number' && item.price < 0) ||
      String(item.id).includes('combo-discount');

    // Group items into "Initial order" and "Added HH:MM" batches (similar to Kitchen Display / kitchen print)
    // Use getCardItemsWithTargets so each item has targetQty for per-card isReady (quantityCompleted >= targetQty)
    const batchesForDisplay = useMemo(() => {
      const batches = order.orderBatches;
      if (!batches?.length) return null;
      const formatBatchTime = (ts: number) =>
        new Date(ts).toLocaleTimeString('en-NZ', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
      return batches
        .map((batch, bi) => {
          const { items: cardItems } = getCardItemsWithTargets(order, bi === 0 ? undefined : bi);
          const batchItems = cardItems.map(({ item, originalIndex, targetQty }) =>
            ({ item, originalIndex, targetQty }));
          if (batchItems.length === 0) return null;
          const label = bi === 0 ? 'Initial order' : `Added ${formatBatchTime(batch.timestamp)}`;
          return { label, batchItems };
        })
        .filter((b): b is NonNullable<typeof b> => b != null && b.batchItems.length > 0);
    }, [order]);

    const isRunningOrderTicket = typeof visibleBatchIndex === 'number' && visibleBatchIndex > 0;

    // Per-batch items for running cards (when visibleBatchIndex >= 1 and that batch exists)
    // Use batch.deltas for correct displayQty so completing one card doesn't affect others
    const visibleBatchInfo = useMemo(() => {
      if (typeof visibleBatchIndex !== 'number' || visibleBatchIndex < 1) return null;
      const batches = order.orderBatches ?? [];
      const batch = batches[visibleBatchIndex];
      const items = order.items ?? [];
      const snapshot = order.lastSentItemSnapshot ?? [];
      if (!batch) return null;
      const snapshotByLineId = new Map(snapshot.map((s) => [s.lineId, s]));
      const initialQty = new Map<string, number>();
      items.forEach((item, idx) => {
        if (item.removed) return;
        const lineId = item.lineId ?? `idx-${idx}`;
        const prev = item.lineId ? snapshotByLineId.get(item.lineId) : snapshot[idx];
        if (prev) initialQty.set(lineId, prev.quantity);
      });
      const getBaseline = (idx: number, lineId: string) => {
        let base = initialQty.get(lineId) ?? 0;
        for (let b = 1; b < visibleBatchIndex; b++) {
          const d = batches[b]?.deltas?.find((x) => x.index === idx);
          if (d && d.deltaQty > 0) base += d.deltaQty;
        }
        return base;
      };
      const deltaMap = new Map<number, number>();
      (batch.deltas ?? []).forEach((d) => {
        if (typeof d.index === 'number' && typeof d.deltaQty === 'number' && d.deltaQty > 0) {
          deltaMap.set(d.index, d.deltaQty);
        }
      });
      const batchItems = batch.itemIndices
        .filter((idx) => idx >= 0 && idx < items.length)
        .map((idx) => {
          const item = items[idx];
          const lineId = item.lineId ?? `idx-${idx}`;
          const baseline = getBaseline(idx, lineId);
          const deltaQty = deltaMap.get(idx);
          const displayQty =
            typeof deltaQty === 'number' && deltaQty > 0
              ? deltaQty
              : Math.max(0, item.quantity - baseline);
          const batchTarget = baseline + displayQty;
          return { item, originalIndex: idx, displayQty, batchTarget };
        })
        .filter(({ item, displayQty }) => !isDiscountItem(item) && !item.removed && displayQty > 0);
      if (batchItems.length === 0) return null;
      const label = `Added ${new Date(batch.timestamp).toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
      return { label, batchItems };
    }, [visibleBatchIndex, order.orderBatches, order.items, order.lastSentItemSnapshot]);

    // Delta items and initial quantities (for main card to show correct original qty)
    const { isNewByLineId, quantityDeltaByLineId, initialQuantityByKey } = useMemo(() => {
      const snapshot = order.lastSentItemSnapshot ?? [];
      const isNew = new Map<string, boolean>();
      const quantityDelta = new Map<string, number>();
      const initialQty = new Map<string, number>();
      const snapshotByLineId = new Map(snapshot.map((s) => [s.lineId, s]));

      (order.items ?? []).forEach((item, idx) => {
        if (item.removed) return;
        const lineId = item.lineId ?? `idx-${idx}`;
        const prev = (item.lineId ? snapshotByLineId.get(item.lineId) : undefined) ?? snapshot[idx];
        isNew.set(lineId, !prev);
        if (prev) {
          initialQty.set(lineId, prev.quantity);
          if (item.quantity > prev.quantity) {
            quantityDelta.set(lineId, item.quantity - prev.quantity);
          }
        }
      });
      return { isNewByLineId: isNew, quantityDeltaByLineId: quantityDelta, initialQuantityByKey: initialQty };
    }, [order.items, order.lastSentItemSnapshot]);

    const deltaDisplayItems = useMemo(() => {
      if (!isRunningOrderTicket) {
        return [] as {
          item: Order['items'][0];
          originalIndex: number;
          displayQty: number;
          isNew: boolean;
        }[];
      }
      const result: {
        item: Order['items'][0];
        originalIndex: number;
        displayQty: number;
        isNew: boolean;
      }[] = [];
      (order.items ?? []).forEach((item, idx) => {
        if (isDiscountItem(item) || item.removed) return;
        const key = item.lineId ?? `idx-${idx}`;
        const isNew = isNewByLineId.get(key) ?? false;
        const deltaQty = quantityDeltaByLineId.get(key) ?? 0;
        if (isNew) {
          result.push({ item, originalIndex: idx, displayQty: item.quantity, isNew: true });
        } else if (deltaQty > 0) {
          result.push({
            item: { ...item, quantity: deltaQty },
            originalIndex: idx,
            displayQty: deltaQty,
            isNew: false,
          });
        }
      });
      return result;
    }, [isRunningOrderTicket, order.items, isNewByLineId, quantityDeltaByLineId]);

    type RunningDisplayItem = {
      item: Order['items'][0];
      originalIndex: number;
      displayQty: number;
      isNew: boolean;
      batchTarget?: number;
    };

    const runningOrderDisplayItems: RunningDisplayItem[] = visibleBatchInfo
      ? visibleBatchInfo.batchItems.map(({ item, originalIndex, displayQty, batchTarget }) => ({
          item,
          originalIndex,
          displayQty,
          isNew: false,
          batchTarget,
        }))
      : deltaDisplayItems.map((d) => {
          const key = (order.items?.[d.originalIndex] as Order['items'][0])?.lineId ?? `idx-${d.originalIndex}`;
          const base = initialQuantityByKey.get(key) ?? 0;
          return { ...d, batchTarget: base + d.displayQty };
        });

    let orderIdentifier: React.ReactNode;
    let orderSubtext: string | null = null;
    const serveableItems = order.items.filter(
        item => !(item.price < 0 || item.id.toString().includes('combo-discount')) && !item.removed
    );
    const allServeableServed = serveableItems.length > 0 && serveableItems.every(item => item.served === true);

    if (order.orderType === 'takeaway') {
        orderIdentifier = `TAKEOUT-${order.takeawayNumber}`;
    } else if (order.orderType === 'buffet') {
        const buffetTypeLabel = order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet';
        const tableName = order.tableName || `Table ${order.tableNumber || order.tableId}`;
        orderIdentifier = (
            <div>
                <div className="flex items-center gap-2">
                    <span>{buffetTypeLabel} BUFFET - {tableName}</span>
                    <span className="text-xs font-bold px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                        BUFFET
                    </span>
                </div>
                {order.customerName && (
                    <div className="text-xs text-pink-600 font-semibold">{order.customerName}</div>
                )}
                {!order.customerName && order.customerNumber && (
                    <div className="text-xs text-pink-600 font-semibold">Customer {order.customerNumber}</div>
                )}
            </div>
        );
        orderSubtext = `${buffetTypeLabel} Buffet`;
    } else if (order.orderType === 'delivery') {
        const deliveryNum = `DELIVERY-${order.deliveryNumber}`;
        if (order.deliveryInfo) {
            orderIdentifier = order.deliveryInfo.platform;
            orderSubtext = `${deliveryNum} - ${order.deliveryInfo.customerName}`;
        } else {
            orderIdentifier = deliveryNum;
        }
    } else if (order.orderType === 'online') {
        // Online web orders (pickup only)
        const hasPickupTime = typeof order.pickupTime === 'number' && !Number.isNaN(order.pickupTime);
        let pickupLabel: string | null = null;
        if (hasPickupTime) {
          const raw = order.pickupTime as number;
          const pickupTimeMs = raw < 1_000_000_000_000 ? raw * 1000 : raw;
          const dt = new Date(pickupTimeMs);
          pickupLabel = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }

        orderIdentifier = (
          <div className="flex items-center flex-wrap gap-2">
            <span>ONLINE ORDER</span>
            {pickupLabel && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-300">
                Pickup {pickupLabel}
              </span>
            )}
          </div>
        );

        if (order.customerName && order.customerName.trim().length > 0) {
            orderSubtext = `${order.customerName} • Order ID: ${order.id.slice(-6)}`;
        } else {
            orderSubtext = `Order ID: ${order.id.slice(-6)}`;
        }
    } else if (order.orderType === 'uber-doordash') {
        // Aggregator: show only service name (Uber Eats or DoorDash)
        orderIdentifier = order.deliveryInfo?.platform ?? 'Uber Eats / DoorDash';
        orderSubtext = order.deliveryInfo?.customerName ? `${order.deliveryInfo.customerName}` : null;
    } else { // 'dine-in'
        const tableName = order.tableName || `Table ${order.tableNumber ?? order.tableId ?? '?'}`;
        if (order.customerNumber) {
            const customerLabel = order.customerName || `Customer ${order.customerNumber}`;
            orderIdentifier = (
                <div>
                    <div>{tableName}</div>
                    <div className="text-xs text-pink-600 font-semibold">{customerLabel}</div>
                </div>
            );
            orderSubtext = customerLabel;
        } else {
            orderIdentifier = tableName;
        }
    }

    const isPickupOrder = order.orderType === 'uber-doordash' || order.orderType === 'delivery';
    const serveActionLabel = isPickupOrder ? 'Mark Picked Up' : 'Mark Served';
    const servedLabel = isPickupOrder ? 'Picked Up' : 'Served';
    const completeButtonLabel = isPickupOrder ? 'Mark Picked Up' : 'Complete Table';
    const completeDoneLabel = isPickupOrder ? 'Marked as picked up' : 'Table Served';
    const orderTypeBadge = (() => {
      if (order.orderType === 'takeaway') return 'TAKEAWAY';
      if (order.orderType === 'uber-doordash') return 'UBER/DOORDASH';
      if (order.orderType === 'delivery') return order.deliveryInfo?.platform?.toUpperCase() || 'DELIVERY';
      if (order.orderType === 'online') return 'ONLINE';
      if (order.orderType === 'buffet') return 'BUFFET';
      return 'DINE IN';
    })();
    const itemProgress = useMemo(() => {
      const { items: cardItems } = getCardItemsWithTargets(order, visibleBatchIndex);
      const batchIdx = typeof visibleBatchIndex === 'number' ? visibleBatchIndex : 0;
      const total = cardItems.length;
      const completed = cardItems.filter(({ item, targetQty }) => {
        const servedQty = getEffectiveQuantityServedForBatch(item, batchIdx, targetQty);
        return servedQty >= targetQty;
      }).length;
      return { completed, total };
    }, [order, visibleBatchIndex]);

    return (
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 mb-3 sm:mb-4">
            <div className="flex justify-between items-center mb-2">
                <div className="flex-1 min-w-0">
                    {isRunningOrderTicket ? (
                      <>
                        <h4 className="font-extrabold text-base sm:text-lg md:text-xl text-red-700 uppercase leading-tight break-words">
                          {visibleBatchInfo ? `RUNNING ORDER #${(visibleBatchIndex ?? 1) + 1}` : 'RUNNING ORDER'}
                        </h4>
                        <div className="mt-0.5">
                          <div className="text-sm sm:text-base text-gray-800 font-semibold leading-tight break-words">
                            {orderIdentifier}
                          </div>
                          {visibleBatchInfo && (
                            <div className="text-sm text-gray-600 mt-0.5">{visibleBatchInfo.label}</div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <h4 className="font-bold text-base sm:text-lg md:text-xl leading-tight break-words">{orderIdentifier}</h4>
                        {orderSubtext && <p className="text-xs text-gray-500 truncate">{orderSubtext}</p>}
                      </>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center rounded bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700">
                        {orderTypeBadge}
                      </span>
                      <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                        Order items {itemProgress.completed}/{itemProgress.total}
                      </span>
                    </div>
                </div>
                <div className="flex flex-col items-end flex-shrink-0 ml-2">
                    {isBuffet && order.buffetEndTime && (
                        <BuffetTimer endTime={order.buffetEndTime} />
                    )}
                    <span className="text-2xl sm:text-3xl font-extrabold leading-none text-blue-700">
                      {itemProgress.completed}/{itemProgress.total}
                    </span>
                    <span className="text-[10px] sm:text-xs font-semibold text-blue-600">items</span>
                    <span className="mt-1 text-xs sm:text-sm text-gray-500 flex items-center">
                        <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> {elapsedTime}
                    </span>
                </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-100 space-y-2 max-h-[48vh] overflow-y-auto pr-1">
              {isRunningOrderTicket ? (
                <ul className="space-y-2">
                  {runningOrderDisplayItems.length > 0 ? (
                    runningOrderDisplayItems.map(({ item, originalIndex, displayQty, isNew, batchTarget }) => {
                      const batchIdx = typeof visibleBatchIndex === 'number' ? visibleBatchIndex : 1;
                      const effectiveCompleted = getEffectiveQuantityCompletedForBatch(item, batchIdx, displayQty);
                      const isReady = batchTarget != null ? effectiveCompleted >= displayQty : effectiveCompleted >= (item.quantity ?? 0);
                      const effectiveServed = getEffectiveQuantityServedForBatch(item, batchIdx, displayQty);
                      const isServed = batchTarget != null ? effectiveServed >= displayQty : Boolean(item.served);
                      return (
                        <li
                          key={item.lineId ?? `${item.id}-${originalIndex}`}
                          className="text-lg flex items-start justify-between gap-3 text-gray-700 rounded-md px-2 py-1 odd:bg-gray-50 even:bg-white"
                        >
                          <div className="flex-1 min-w-0 break-words">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{displayQty}x</span> {item.name}
                              {isNew && (
                                <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded">NEW</span>
                              )}
                              {item.selectedMeatOption && (
                                <span className="ml-2 text-xs text-pink-600 bg-pink-100 px-1.5 py-0.5 rounded">
                                  {item.selectedMeatOption}
                                </span>
                              )}
                              {!isReady && (
                                <span className="ml-2 text-xs text-yellow-600 font-semibold">(Waiting)</span>
                              )}
                            </div>
                            {item.notes && (
                              <div className="mt-1.5">
                                <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-2 py-1.5 shadow-sm">
                                  <p className="text-[10px] font-bold text-yellow-900 mb-0.5 flex items-center">
                                    <span className="mr-1">📝</span> Special Instructions:
                                  </p>
                                  <p className="text-xs font-medium text-yellow-900">{item.notes}</p>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {isServed && (
                              <span className="text-xs text-green-600 font-semibold flex items-center gap-1">
                                <CheckCircleIcon className="w-4 h-4" /> {servedLabel}
                              </span>
                            )}
                            {!isServed && onUpdateItemStatus && (
                              <button
                                type="button"
                                disabled={!isReady || order.status === 'served'}
                                onClick={() => onUpdateItemStatus(order.id, originalIndex, true, batchTarget, batchIdx, displayQty)}
                                className={`text-sm font-semibold px-3 py-1.5 rounded ${
                                  isReady && order.status !== 'served'
                                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                }`}
                              >
                                {serveActionLabel}
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })
                  ) : (
                    <li className="text-sm text-gray-500 italic">No items to add.</li>
                  )}
                </ul>
              ) : batchesForDisplay ? (
                (visibleBatchIndex != null && batchesForDisplay[visibleBatchIndex]
                  ? [batchesForDisplay[visibleBatchIndex]]
                  : [batchesForDisplay[0]]
                ).map((batch) => (
                  <div key={batch.label} className="mb-2 last:mb-0">
                    <p className="text-xs font-semibold text-gray-600 border-b border-gray-200 pb-1">
                      {batch.label}
                    </p>
                    <ul className="space-y-2 mt-1">
                      {batch.batchItems.map(({ item, originalIndex, targetQty }) => {
                        if (item.removed) {
                          return (
                            <li
                              key={`${item.id}-${originalIndex}-removed`}
                              className="text-sm flex items-center gap-2 text-gray-400"
                            >
                              <span className="font-semibold">{item.quantity}x</span>
                              <span className="line-through">{item.name}</span>
                              <span className="text-xs font-semibold text-gray-500">(removed)</span>
                            </li>
                          );
                        }

                        const batchIdx = visibleBatchIndex ?? 0;
                        const effectiveCompleted = getEffectiveQuantityCompletedForBatch(item, batchIdx, targetQty);
                        const isReady = effectiveCompleted >= targetQty;
                        const effectiveServed = getEffectiveQuantityServedForBatch(item, batchIdx, targetQty);
                        const isServed = effectiveServed >= targetQty;
                        const keyForLine = item.lineId ?? `idx-${originalIndex}`;
                        // Main card (batch 0): show initial quantity (targetQty), not total. Matches Kitchen Display.
                        const displayQty = batchIdx === 0 ? targetQty : item.quantity;

                        return (
                          <li
                            key={`${item.id}-${originalIndex}`}
                            className="text-lg flex items-start justify-between gap-3 text-gray-700 rounded-md px-2 py-1 odd:bg-gray-50 even:bg-white"
                          >
                            <div className="flex-1 min-w-0 break-words">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{displayQty}x</span> {item.name}
                                {item.isRemake && (
                                  <span className="text-xs font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded border border-purple-300">
                                    🔄 REMAKE
                                  </span>
                                )}
                                {item.selectedMeatOption && (
                                  <span className="ml-2 text-xs text-pink-600 bg-pink-100 px-1.5 py-0.5 rounded">
                                    {item.selectedMeatOption}
                                  </span>
                                )}
                                {!isReady && (
                                  <span className="ml-2 text-xs text-yellow-600 font-semibold">(Waiting)</span>
                                )}
                              </div>
                              {item.isRemake && item.remakeReason && (
                                <p className="text-xs text-purple-600 mt-0.5">
                                  Reason: {item.remakeReason}
                                </p>
                              )}
                              {item.notes && (
                                <div className="mt-1.5">
                                  <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-2 py-1.5 shadow-sm">
                                    <p className="text-[10px] font-bold text-yellow-900 mb-0.5 flex items-center">
                                      <span className="mr-1">📝</span>
                                      Special Instructions:
                                    </p>
                                    <p className="text-xs font-medium text-yellow-900">{item.notes}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {isServed && (
                                <span className="text-xs text-green-600 font-semibold flex items-center gap-1">
                                  <CheckCircleIcon className="w-4 h-4" /> {servedLabel}
                                </span>
                              )}
                              {!isServed && onUpdateItemStatus && (
                                <button
                                  type="button"
                                  disabled={!isReady || order.status === 'served'}
                                  onClick={() => onUpdateItemStatus(order.id, originalIndex, true, targetQty, batchIdx, targetQty)}
                                  className={`text-sm font-semibold px-3 py-1.5 rounded ${
                                    isReady && order.status !== 'served'
                                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                  }`}
                                >
                                  {serveActionLabel}
                                </button>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))
              ) : (
                <ul className="space-y-2">
                  {order.items.map((item, index) => {
                    const isDiscount = isDiscountItem(item);
                    if (isDiscount) return null;

                    if (item.removed) {
                      return (
                        <li
                          key={`${item.id}-${index}-removed`}
                          className="text-sm flex items-center gap-2 text-gray-400"
                        >
                          <span className="font-semibold">{item.quantity}x</span>
                          <span className="line-through">{item.name}</span>
                          <span className="text-xs font-semibold text-gray-500">(removed)</span>
                        </li>
                      );
                    }

                    const effectiveCompleted = getEffectiveQuantityCompletedForBatch(item, undefined, item.quantity);
                    const isReady = effectiveCompleted >= item.quantity;
                    const effectiveServed = getEffectiveQuantityServedForBatch(item, undefined, item.quantity);
                    const isServed = effectiveServed >= item.quantity;
                    const keyForLine = item.lineId ?? `idx-${index}`;
                    const displayQty = initialQuantityByKey.has(keyForLine)
                      ? initialQuantityByKey.get(keyForLine)!
                      : item.quantity;

                    return (
                      <li
                        key={`${item.id}-${index}`}
                        className="text-lg flex items-start justify-between gap-3 text-gray-700 rounded-md px-2 py-1 odd:bg-gray-50 even:bg-white"
                      >
                        <div className="flex-1 min-w-0 break-words">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{displayQty}x</span> {item.name}
                            {item.isRemake && (
                              <span className="text-xs font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded border border-purple-300">
                                🔄 REMAKE
                              </span>
                            )}
                            {item.selectedMeatOption && (
                              <span className="ml-2 text-xs text-pink-600 bg-pink-100 px-1.5 py-0.5 rounded">
                                {item.selectedMeatOption}
                              </span>
                            )}
                            {!isReady && (
                              <span className="ml-2 text-xs text-yellow-600 font-semibold">(Waiting)</span>
                            )}
                          </div>
                          {item.isRemake && item.remakeReason && (
                            <p className="text-xs text-purple-600 mt-0.5">Reason: {item.remakeReason}</p>
                          )}
                          {item.notes && (
                            <div className="mt-1.5">
                              <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-2 py-1.5 shadow-sm">
                                <p className="text-[10px] font-bold text-yellow-900 mb-0.5 flex items-center">
                                  <span className="mr-1">📝</span>
                                  Special Instructions:
                                </p>
                                <p className="text-xs font-medium text-yellow-900">{item.notes}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isServed && (
                            <span className="text-xs text-green-600 font-semibold flex items-center gap-1">
                              <CheckCircleIcon className="w-4 h-4" /> {servedLabel}
                            </span>
                          )}
                          {!isServed && onUpdateItemStatus && (
                            <button
                              type="button"
                              disabled={!isReady || order.status === 'served'}
                              onClick={() => onUpdateItemStatus(order.id, index, true, item.quantity)}
                              className={`text-sm font-semibold px-3 py-1.5 rounded ${
                                isReady && order.status !== 'served'
                                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              {serveActionLabel}
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {showCompleteTable && order.status !== 'served' && serveableItems.length > 0 && (
              <button 
                onClick={() => onUpdateStatus(order.id, 'served')}
                disabled={!allServeableServed}
                className={`w-full mt-4 text-sm font-bold py-2 rounded-lg transition-colors flex items-center justify-center ${
                  allServeableServed
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                <CheckCircleIcon className="w-5 h-5 mr-2" /> {completeButtonLabel}
              </button>
            )}
            {showCompleteTable && order.status === 'served' && (
              <div className="w-full mt-4 bg-green-100 text-green-700 text-sm font-bold py-2 rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="w-5 h-5 mr-2" /> {completeDoneLabel}
              </div>
            )}
        </div>
    );
};

const isDiscountItemForColumn = (item: Order['items'][0]) =>
  (typeof item.price === 'number' && item.price < 0) ||
  String(item.id).includes('combo-discount');

const OrderStatusColumn: React.FC<OrderStatusColumnProps> = ({ title, orders = [], tickets: ticketsProp, onUpdateStatus, onUpdateItemStatus }) => {
  const getHeaderColor = () => {
    if (title.includes('New')) return 'border-red-500';
    if (title.includes('Preparation')) return 'border-yellow-500';
    if (title.includes('Ready')) return 'border-green-500';
    return 'border-gray-300';
  };

  const tickets: ServiceTicket[] = ticketsProp ?? orders.flatMap((order) => {
    const baseTicket = { order, batchIndex: undefined as number | undefined, showCompleteTable: true };
    const snapshot = order.lastSentItemSnapshot ?? [];
    const items = order.items ?? [];
    const batches = order.orderBatches ?? [];

    const hasSnapshotAndDeltas = snapshot.length > 0 && (() => {
      const snapshotByLineId = new Map(snapshot.map((s) => [s.lineId, s]));
      const currentNonRemoved = items.filter((i) => !i.removed);
      const hasNewOrDelta = currentNonRemoved.some((item, idx) => {
        const prev = item.lineId ? snapshotByLineId.get(item.lineId) : undefined;
        const prevByIndex = snapshot[idx];
        const prevQty = prev ?? prevByIndex;
        if (!prevQty) return true;
        return item.quantity > prevQty.quantity;
      });
      const hasRemoved = snapshot.length > currentNonRemoved.length || snapshot.some(
        (s) => !currentNonRemoved.some((i) => i.lineId === s.lineId)
      );
      return hasNewOrDelta || hasRemoved;
    })();
    const hasMultipleBatches = batches.length > 1;

    if (!hasSnapshotAndDeltas && !hasMultipleBatches) return [baseTicket];

    // Always show main order card so user can mark items served and Complete Table
    const runningTickets =
      batches.length > 1
        ? batches.slice(1).map((batch, i) => ({
            order,
            batchIndex: i + 1 as number,
            batchId: batch.batchId,
            showCompleteTable: false, // only main card has Complete Table
          }))
        : hasSnapshotAndDeltas
          ? [{ order, batchIndex: 1 as number, batchId: undefined as string | undefined, showCompleteTable: false }]
          : [];
    return [baseTicket, ...runningTickets];
  });

  return (
    <div className="bg-gray-100 rounded-lg w-full min-w-0 h-full flex flex-col">
      <div className={`p-4 sm:p-5 border-b-4 ${getHeaderColor()}`}>
        <h3 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center">
            {title}
            <span className="ml-2 bg-gray-300 text-gray-700 text-sm font-semibold rounded-full px-2.5 py-0.5">{tickets.length}</span>
        </h3>
      </div>
      <div className="p-4 sm:p-5 flex-1 overflow-y-auto min-h-0">
        {tickets.length > 0 ? (
            tickets.map(({ order, batchIndex, batchId, showCompleteTable }) => (
              <OrderCard
                key={batchIndex == null ? `${order.id}-base` : `${order.id}-run-${batchId ?? batchIndex}`}
                order={order}
                visibleBatchIndex={batchIndex}
                showCompleteTable={showCompleteTable}
                onUpdateStatus={onUpdateStatus}
                onUpdateItemStatus={onUpdateItemStatus}
              />
            ))
        ) : (
            <div className="h-full flex items-center justify-center">
                <p className="text-base text-gray-500">No orders here.</p>
            </div>
        )}
      </div>
    </div>
  );
};

export default OrderStatusColumn;